/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useMemo, useCallback } from "react";
import { usePermission } from "../../../hooks/usePermissionTabs";
import { useForm } from "react-hook-form";
import { useAllFMMerchantData } from "../../../apis/merchantApis";
import { useSalesChannelByMerchant } from "../../../apis/integrationApis";
import {
  useProductSyncMutation,
  useProductAddMutation,
  ProductAddPayloadSchema,
} from "../../../hooks/productFetchHooks";
import { useSelector } from "react-redux";
import {
  IconPlus,
  IconChevronDown,
  IconChevronUp,
  IconArrowLeft,
  IconRefresh,
  IconShoppingCart,
  IconX,
} from "@tabler/icons-react";
import toast from "react-hot-toast";
import EntriesSelector from "../../../components/common/EntriesSelector";
import NoDataFound from "../../../components/common/NoDataFound";
import TableSkeleton from "../../../components/skeleton/TableSkeleton";
import Pagination from "../../../components/common/Pagination";
import SelectInput from "../../../components/Select";
import ButtonStyled from "../../../components/common/ButtonStyled";
import AddProductFetchModal from "./AddProductFetchModal";
import {
  useProductCategoryAllListData,
  useProductSubCategoriesListData,
} from "../../../apis/productApis";
import CardSkeleton from "../../../components/skeleton/CardSkeleton";
import { useLanguage } from "../../../context/LanguageContext";

interface ProductRequestModalProps {
  setActiveTab: React.Dispatch<React.SetStateAction<string>>;
}

export default function ProductFetch({
  setActiveTab,
}: ProductRequestModalProps) {
  const { t } = useLanguage();
  const { allowed: canCreateProduct } = usePermission("products", "create");
  const { allowed: canUpdateProduct } = usePermission("products", "update");
  const {
    register,
    handleSubmit,
    clearErrors,
    setValue,
    watch,
    formState: { errors },
  } = useForm<any>();
  const [isOpen, setIsOpen] = useState(false);
  const [showSearch, setShowSearch] = useState<boolean>(false);

  const {
    register: addProductRegister,
    handleSubmit: handleAddProductSubmit,
    clearErrors: handleProductClearErrors,
    setValue: setProductValue,
    watch: productWatch,
    formState: { errors: productErrors },
  } = useForm<any>();

  const [pageNo, setPageNo] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [searchFor, setSearchFor] = useState<string>("");
  const [actionModal, setActionModal] = useState<boolean>(false);
  const selectedMerchantId = watch("allMerchants");
  const { data: salesChannels } = useSalesChannelByMerchant(selectedMerchantId);
  const { data: allMerchants } = useAllFMMerchantData();
  const [products, setProducts] = useState<any>();
  const [selectedOptionValues, setSelectedOptionValues] = useState<string[]>(
    [],
  );
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  const { mutate: fetchProduct, isPending: isLoadingProductFetch } =
    useProductSyncMutation(pageNo, limit);

  const { data: allCategory } = useProductCategoryAllListData();
  const { data: subCategories } = useProductSubCategoriesListData(
    productWatch("category") || "",
  );

  const { mutate: addProduct, isPending: isAddingProduct } =
    useProductAddMutation();

  const profile = useSelector((state: any) => state.auth.profile);

  const getProductOptionValueKeys = useCallback((product: any) => {
    if (product.consisted_products?.length > 0) {
      return product.consisted_products.map(
        (c: any) => `${product.id}-consisted-${c.id}`,
      );
    }

    if (product.options?.length > 0) {
      return product.options.flatMap((option: any) =>
        (option.values || []).map((value: any) => {
          const valueName =
            typeof value.name === "object"
              ? value.name?.en || value.name?.ar
              : value.name;
          return `${product.id}-option-${option.id || option.name}-valuename-${valueName}`;
        }),
      );
    }

    return [`${product.id}-product`];
  }, []);

  const allProductsOnPage = products?.products || [];
  const allOptionValueKeysOnPage = useMemo(
    () => allProductsOnPage.flatMap((p: any) => getProductOptionValueKeys(p)),
    [allProductsOnPage, getProductOptionValueKeys],
  );

  const selectedOnPage = useMemo(
    () =>
      selectedOptionValues.filter((id) =>
        allOptionValueKeysOnPage.includes(id),
      ),
    [selectedOptionValues, allOptionValueKeysOnPage],
  );
  const allSelected =
    allOptionValueKeysOnPage.length > 0 &&
    allOptionValueKeysOnPage.every((id: string) =>
      selectedOptionValues.includes(id),
    );
  const someSelected = selectedOnPage.length > 0 && !allSelected;

  const handleSelectAll = useCallback(
    (checked: boolean) => {
      setSelectedOptionValues((prev) => {
        if (checked) {
          const toAdd = allOptionValueKeysOnPage.filter(
            (id: any) => !prev.includes(id),
          );
          return Array.from(new Set([...prev, ...toAdd]));
        } else {
          return prev.filter((id) => !allOptionValueKeysOnPage.includes(id));
        }
      });
    },
    [allOptionValueKeysOnPage],
  );

  const handleSelectProductOptionValues = useCallback(
    (productId: string, checked: boolean) => {
      const product = allProductsOnPage.find((p: any) => p.id === productId);
      if (!product) return;

      const productOptionValueKeys = getProductOptionValueKeys(product);
      setSelectedOptionValues((prev) => {
        if (checked) {
          const isBundle =
            product.consisted_products && product.consisted_products.length > 0;
          const keysToAdd = isBundle
            ? product.consisted_products.map(
                (consisted: any) => `${product.id}-consisted-${consisted.id}`,
              )
            : productOptionValueKeys;
          const toAdd = keysToAdd.filter((id: any) => !prev.includes(id));
          return Array.from(new Set([...prev, ...toAdd]));
        } else {
          return prev.filter((id) => !productOptionValueKeys.includes(id));
        }
      });
    },
    [allProductsOnPage, getProductOptionValueKeys],
  );

  const handleSelectOptionValue = useCallback(
    (
      productId: string,
      optionId: string,
      valueName: string,
      checked: boolean,
    ) => {
      const key = `${productId}-option-${optionId}-valuename-${valueName}`;
      setSelectedOptionValues((prev) =>
        checked
          ? Array.from(new Set([...prev, key]))
          : prev.filter((id) => id !== key),
      );
    },
    [],
  );

  const toggleRow = (productId: string) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      const idNum = parseInt(productId, 10);
      if (newSet.has(idNum)) {
        newSet.delete(idNum);
      } else {
        newSet.add(idNum);
      }
      return newSet;
    });
  };

  const [columnsData, setColumnsData] = useState([
    { text: "Checkbox", value: "checkbox", isShow: true },
    { text: "S/N", value: "sn", isShow: true },
    { text: "Product Name", value: "name", isShow: true },
    { text: "SKU", value: "sku", isShow: true },
    { text: "Category", value: "category", isShow: true },
    { text: "Price", value: "price", isShow: true },
    { text: "Already Exist", value: "alreadyExist", isShow: true },
    { text: "Type", value: "parent", isShow: true },
    { text: "Actions", value: "actions", isShow: true },
  ]);

  const setHeadings = (value: string | undefined, checked: boolean) => {
    setColumnsData((prev) =>
      prev.map((col) =>
        col.value === value ? { ...col, isShow: checked } : col,
      ),
    );
  };

  const renderExpandedContent = (product: any) => {
    const idNum = parseInt(product.id, 10);
    if (!expandedRows.has(idNum)) return null;

    const hasOptions = product.options && product.options.length > 0;
    const hasConsistedProducts =
      product.consisted_products && product.consisted_products.length > 0;
    const productOptionValueKeys = getProductOptionValueKeys(product);
    const selectedCount = productOptionValueKeys.filter((id: string) =>
      selectedOptionValues.includes(id),
    ).length;
    const allSelectedForProduct =
      selectedCount === productOptionValueKeys.length;

    const getVariantSku = (optionId: string, value: any) => {
      if (!product.skus || product.skus.length === 0) {
        return "";
      }

      const valueName =
        typeof value.name === "object"
          ? value.name?.en || value.name?.ar
          : value.name;

      const skuObj = product.skus.find((s: any) =>
        s.related_option_values?.some((ov: any) => {
          const ovName = ov?.en || ov?.ar;
          return ovName === valueName;
        }),
      );

      return skuObj?.sku || "";
    };

    return (
      <div className="bg-white p-6 border-t border-b border-gray-100">
        <div className="space-y-6">
          {hasConsistedProducts && (
            <div className="border border-gray-200 rounded-xl p-5 bg-white shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h5 className="font-semibold text-gray-700 text-sm uppercase tracking-wide flex items-center space-x-2">
                  <span className="w-2 h-2 bg-indigo-500 rounded-full"></span>
                  <span>{t("Products")} ({product.consisted_products.length})</span>
                </h5>
              </div>
              <div className="flex flex-wrap gap-2">
                {product.consisted_products.map((cp: any, index: number) => {
                  const cpKey = `${product.id}-consisted-${cp.id}`;
                  const isSelected = selectedOptionValues.includes(cpKey);
                  return (
                    <label
                      key={cpKey || index}
                      className={`flex items-center space-x-2 cursor-pointer p-2 rounded-lg ${
                        isSelected
                          ? "bg-indigo-50 border-indigo-200 text-indigo-700"
                          : "bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100"
                      }`}
                    >
                      <div className="flex flex-col">
                        <span className="text-xs font-medium px-2 py-1 rounded-md bg-white border border-gray-200">
                          {typeof cp.name === "object"
                            ? cp.name?.en || cp.name?.ar
                            : cp.name || "Unnamed Product"}
                        </span>
                        <span className="text-xs text-gray-500 mt-1">
                          {t("SKU")}: {cp.sku || ""}
                        </span>
                        <span className="text-xs text-gray-500 mt-1">
                          {t("Price")}: {cp.price?.amount || 0}{" "}
                          {cp.price?.currency || ""}
                        </span>
                        <span className="text-xs text-gray-500 mt-1">
                          {t("Quantity")}: {cp.quantity || 1}
                        </span>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          {!hasConsistedProducts && hasOptions && (
            <div className="space-y-4">
              {product.options.map((option: any, index: number) => (
                <div
                  key={option.id || index}
                  className="border border-gray-200 rounded-xl p-5 bg-white shadow-sm"
                >
                  <div className="flex items-center justify-between mb-4">
                    <h5 className="font-bold text-[#131327] text-xs flex items-center space-x-2">
                      <span className="w-1.5 h-1.5 bg-[#4F46E5] rounded-full"></span>
                      <span>
                        <span className="uppercase">{option.name}</span>
                        <span className="text-gray-400 font-normal ml-1">
                          ({option.values?.length || 0})
                        </span>
                      </span>
                    </h5>
                    {option.values && option.values.length > 0 && (
                      <label className="flex items-center space-x-1.5 text-xs font-semibold text-gray-600 cursor-pointer hover:text-blue-600 select-none">
                        <input
                          type="checkbox"
                          checked={option.values.every((v: any) => {
                            const vName =
                              typeof v.name === "object"
                                ? v.name?.en || v.name?.ar
                                : v.name;
                            const vKey = `${product.id}-option-${option.id || option.name}-valuename-${vName}`;
                            return selectedOptionValues.includes(vKey);
                          })}
                          onChange={(e) => {
                            option.values?.forEach((v: any) => {
                              const vName =
                                typeof v.name === "object"
                                  ? v.name?.en || v.name?.ar
                                  : v.name;
                              handleSelectOptionValue(
                                product.id,
                                option.id || option.name,
                                vName,
                                e.target.checked,
                              );
                            });
                          }}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-4 w-4"
                        />
                        <span>{t("All")}</span>
                      </label>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2.5 mt-2">
                    {option.values && option.values.length > 0 ? (
                      option.values.map((value: any, valueIndex: number) => {
                        const variantSku = getVariantSku(
                          option.id || option.name,
                          value,
                        );
                        const vName =
                          typeof value.name === "object"
                            ? value.name?.en || value.name?.ar
                            : value.name;
                        const vKey = `${product.id}-option-${option.id || option.name}-valuename-${vName}`;
                        const isSelected = selectedOptionValues.includes(vKey);
                        return (
                          <label
                            key={vKey || valueIndex}
                            className={`flex items-center space-x-3 cursor-pointer p-3 px-4 rounded-xl border transition-all duration-150 ${
                              isSelected
                                ? "bg-blue-50 border-blue-200 text-blue-700 font-semibold shadow-sm"
                                : "bg-white border-gray-200 text-gray-700 hover:bg-gray-50"
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={(e) =>
                                handleSelectOptionValue(
                                  product.id,
                                  option.id || option.name,
                                  vName,
                                  e.target.checked,
                                )
                              }
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-4 w-4"
                            />
                            <div className="flex flex-col ml-1">
                              <span className="text-xs font-semibold">
                                {typeof value.name === "object"
                                  ? value.name?.en || value.name?.ar
                                  : value.name || "Unnamed Value"}
                              </span>
                              <span className="text-[10px] text-gray-400 mt-0.5 font-normal">
                                {t("SKU")}: {variantSku}
                              </span>
                            </div>
                          </label>
                        );
                      })
                    ) : (
                      <div className="col-span-full text-center py-4 text-gray-400 italic text-sm">
                        {t("No values available for this option")}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {!hasConsistedProducts && !hasOptions && (
            <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 bg-white text-center shadow-sm">
              <div className="flex flex-col items-center space-y-3">
                <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                  <span className="text-gray-400 text-sm font-medium">✓</span>
                </div>
                <label className="flex items-center justify-center space-x-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={selectedOptionValues.includes(
                      `${product.id}-product`,
                    )}
                    onChange={(e) =>
                      setSelectedOptionValues((prev) =>
                        e.target.checked
                          ? Array.from(
                              new Set([...prev, `${product.id}-product`]),
                            )
                          : prev.filter((id) => id !== `${product.id}-product`),
                      )
                    }
                    className="rounded border-gray-300 text-indigo-600 shadow-sm focus:ring-indigo-500 h-5 w-5"
                  />
                  <span className="text-sm font-semibold text-gray-700 group-hover:text-indigo-600">
                    {t("Select Entire Product (No Options or Consisted Products)")}
                  </span>
                </label>
                <p className="text-xs text-gray-500">
                  {t("SKU")}: {product.sku || ""}
                </p>
                <p className="text-xs text-gray-500">
                  {t("This product has no configurable options or consisted products.")}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const fetchProducts = (data: any) => {
    const payload = {
      clientId: data?.allMerchants,
      merchantId: data?.salesChannels,
      superUserId: profile?.superUserId,
      warehouseId: "996466bd-dff9-4ee0-af47-05947b2bed9c",
    };
    fetchProduct(payload, {
      onSuccess: (data: any) => {
        setProducts(data);
        setSelectedOptionValues([]);
        setExpandedRows(new Set<number>());
        toast.success(t("Product fetched successfully"));
        setActionModal(false);
      },
      onError: (error) => {
        toast.error(t(`${error}`));
      },
    });
  };

  const handleAddProduct = (formData: any) => {
    if (!products?.products || selectedOptionValues.length === 0) {
      console.error("No products or variants selected to add.");
      toast.error(t("Please select at least one product or variant."));
      return;
    }

    const groupedSelections: {
      [productId: string]: {
        isSimple: boolean;
        selectedOptions: { [optionId: string]: string[] };
        selectedConsistedProducts: string[];
        productData: any;
      };
    } = {};

    selectedOptionValues.forEach((key) => {
      let productIdString: string;
      let product: any;

      if (key.endsWith("-product")) {
        productIdString = key.replace("-product", "");
        product = products.products.find((p: any) => p.id === productIdString);

        if (product && !groupedSelections[productIdString]) {
          groupedSelections[productIdString] = {
            isSimple: true,
            selectedOptions: {},
            selectedConsistedProducts: [],
            productData: product,
          };
        }
        return;
      }

      const optionParts = key.split("-option-");
      if (optionParts.length === 2) {
        productIdString = optionParts[0];
        const optionValuePart = optionParts[1];
        product = products.products.find((p: any) => p.id === productIdString);

        if (!product) return;

        if (!groupedSelections[productIdString]) {
          groupedSelections[productIdString] = {
            isSimple: false,
            selectedOptions: {},
            selectedConsistedProducts: [],
            productData: product,
          };
        }

        // ✅ split on -valuename- instead of -value-
        const valueParts = optionValuePart.split("-valuename-");
        if (valueParts.length === 2) {
          const optionId = valueParts[0];
          const valueName = valueParts[1]; // ✅ this is now the actual name string

          if (!groupedSelections[productIdString].selectedOptions[optionId]) {
            groupedSelections[productIdString].selectedOptions[optionId] = [];
          }
          groupedSelections[productIdString].selectedOptions[optionId].push(
            valueName,
          );
        }
      }

      const consistedParts = key.split("-consisted-");
      if (consistedParts.length === 2) {
        productIdString = consistedParts[0];
        const consistedId = consistedParts[1];
        product = products.products.find((p: any) => p.id === productIdString);

        if (!product) {
          console.warn(`Product with ID ${productIdString} not found`);
          return;
        }

        if (!groupedSelections[productIdString]) {
          groupedSelections[productIdString] = {
            isSimple: false,
            selectedOptions: {},
            selectedConsistedProducts: [],
            productData: product,
          };
        }

        groupedSelections[productIdString].selectedConsistedProducts.push(
          consistedId,
        );
      }
    });

    const validGroupedSelections = Object.entries(groupedSelections).filter(
      ([_, selection]) =>
        selection.isSimple ||
        Object.keys(selection.selectedOptions).length > 0 ||
        selection.selectedConsistedProducts.length > 0,
    );

    if (validGroupedSelections.length === 0) {
      console.error(
        "No valid products, variants, or consisted products selected.",
      );
      toast.error(
        t("No valid products, variants, or consisted products selected."),
      );
      return;
    }

    const getOptionDetails = (productData: any, valueName: string) => {
      for (const option of productData.options || []) {
        const value = option.values.find((v: any) => {
          const vName =
            typeof v.name === "object" ? v.name?.en || v.name?.ar : v.name;
          return vName === valueName; // ✅ match by name
        });

        if (value) {
          // ✅ related_option_values contains {ar: 'Green'} style objects — match by name
          const sku = productData.skus?.find((s: any) =>
            s.related_option_values?.some((ov: any) => {
              const ovName = typeof ov === "object" ? ov?.en || ov?.ar : ov;
              return ovName === valueName;
            }),
          );

          return {
            optionId: String(option.id || option.name),
            optionName: option.name,
            valueId: valueName, // use name as stable identifier
            valueName: valueName,
            sku: sku?.sku || null,
          };
        }
      }
      return null;
    };

    const getConsistedProductDetails = (
      productData: any,
      consistedId: string,
    ) => {
      const consisted = productData.consisted_products?.find(
        (cp: any) => String(cp.id) === String(consistedId),
      );
      if (consisted) {
        return {
          productId: String(consisted.id),
          name: consisted.name?.en || consisted.name?.ar || "Unnamed",
          sku: consisted.sku || null,
          price: Number(consisted.price?.amount || 0),
          thumbnail: consisted.thumbnail || consisted.main_image || null,
          quantity: Number(consisted.quantity || 1), // Include quantity, default to 1 if not provided
        };
      }
      return null;
    };

    const selectedCategory = allCategory?.find(
      (category: any) => category?.category === formData?.category,
    );
    if (!selectedCategory) {
      console.error("Category not found");
      toast.error(t("Please select a valid category."));
      return;
    }

    const payload = {
      categoryId: String(selectedCategory.id),
      subCategoryId: String(formData.subCategory),
      storageType: formData.storageType,
      capacity: formData.capacity,
      clientId: watch("allMerchants"),
      merchantId: watch("salesChannels"),
      superUserId: profile?.superUserId,
      warehouseId: "996466bd-dff9-4ee0-af47-05947b2bed9c",
      items: validGroupedSelections.map(([productId, selection]) => {
        let variants: any[] = [];
        let consistedProducts: any[] = [];
        let type: string;

        // Determine product type
        if (selection.productData.consisted_products?.length > 0) {
          type = "bundle";
        } else if (
          selection.productData.options &&
          selection.productData.options.length > 0
        ) {
          type = "variant";
        } else {
          type = "simple";
        }

        if (selection.isSimple) {
          variants = [
            {
              related_option_values: [],
              isSimple: true,
              sku: selection.productData.sku || null, // 👈 important
            },
          ];
        } else {
          const selectedValueIds = Object.values(
            selection.selectedOptions,
          ).flat();
          variants = selectedValueIds
            .map((valueId) => getOptionDetails(selection.productData, valueId))
            .filter(Boolean);

          consistedProducts = selection.selectedConsistedProducts
            .map((consistedId) =>
              getConsistedProductDetails(selection.productData, consistedId),
            )
            .filter(Boolean);
        }

        const uniqueVariants = Array.from(
          new Set(variants.map((variant) => JSON.stringify(variant))),
        ).map((jsonString) => JSON.parse(jsonString));

        return {
          productId,
          name:
            selection.productData.name?.en ||
            selection.productData.name?.ar ||
            "Unnamed Product",
          sku: selection.productData.sku || null,
          price: Number(selection.productData.price?.amount),
          regularPrice: Number(selection.productData.regular_price?.amount),
          thumbnail:
            selection.productData.thumbnail ||
            selection.productData.main_image ||
            null,
          type,
          ...(type !== "simple" &&
            uniqueVariants.length > 0 && { variants: uniqueVariants }),
          ...(consistedProducts.length > 0 && { consistedProducts }),
        };
      }),
    };

    const parsedPayload = ProductAddPayloadSchema.safeParse(payload);
    if (!parsedPayload.success) {
      console.error("Payload validation failed:", parsedPayload.error);
      toast.error(t("Invalid product data. Please check your selections."));
      return;
    }

    addProduct(parsedPayload.data, {
      onSuccess: () => {
        toast.success(t("Product added successfully"));
        setIsOpen(false);
        setActiveTab("Item");
      },
      onError: (error) => {
        toast.error(t(`${error}`));
      },
    });
  };

  const handlePaginationFetch = (page: number) => {
    setPageNo(page);

    const currentFormData = {
      client: watch("allMerchants"),
      salesChannels: watch("salesChannels"),
      category: watch("category"),
      subCategory: watch("subCategory"),
      storageType: watch("storageType"),
      capacity: watch("capacity"),
    };

    fetchProducts(currentFormData);
  };

  return (
    <div className="bg-white rounded-3xl border border-[#ECECEC] shadow-sm overflow-hidden w-full">
      {/* Header with toolbar */}
      <div className="px-6 py-4 flex flex-col md:flex-row md:items-center justify-between border-b border-[#ECECEC] gap-4 bg-white">
        <h2 className="text-xl font-semibold text-[#212121]"> {t("Product Fetch")}</h2>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setActiveTab("Item")}
            className="p-2 rounded-lg border border-[#D2D2D2]"
            title={t("Back to Items")}
          >
            <IconX size={20} stroke={2} />
          </button>
          {canCreateProduct && (
            <button
              onClick={() => setIsOpen(true)}
              disabled={selectedOptionValues.length === 0}
              className={`p-2 rounded-lg ${
                selectedOptionValues.length > 0
                  ? "bg-[#FBD037] text-[#212121]"
                  : "bg-slate-100 text-slate-400 cursor-not-allowed"
              }`}
            >
              <IconPlus size={20} stroke={2} />
            </button>
          )}
        </div>
      </div>

      {/* Main content area */}
      <div className="p-4 bg-[#F7F6F2]/40 w-full space-y-4">
        <form
          className="grid grid-cols-5 gap-4"
          onSubmit={handleSubmit(fetchProducts)}
        >
          <SelectInput
            className="col-span-2"
            inputClassName="rounded-full text-xs h-9 py-0.5 border-slate-200 bg-slate-50 focus:bg-white"
            name="allMerchants"
            placeholder={t("Merchant")}
            value={watch("allMerchants")}
            register={register}
            error={errors?.category}
            clearErrors={clearErrors}
            validation={{ required: t("Please select Merchant") }}
            onChange={(val) => setValue("allMerchants", val)}
            selectOptions={
              allMerchants?.map((merchant: any) => ({
                id: merchant?.id,
                value: merchant?.id,
                text: merchant?.name,
              })) || []
            }
          />
          <SelectInput
            className="col-span-2"
            inputClassName="rounded-full text-xs h-9 py-0.5 border-slate-200 bg-slate-50 focus:bg-white"
            name="salesChannels"
            placeholder={t("Sales Channel")}
            value={watch("salesChannels")}
            register={register}
            error={errors?.subcategory}
            clearErrors={clearErrors}
            validation={{ required: t("Please select Sales Channels") }}
            onChange={(val) => setValue("salesChannels", val)}
            selectOptions={
              salesChannels?.map((sales: any) => ({
                id: sales?.id,
                value: sales?.id,
                text: sales?.platformName,
              })) || []
            }
          />
          {canUpdateProduct && (
            <ButtonStyled
              value={t("Fetch")}
              type="submit"
              extraClasses="py-1.5 px-5 text-xs font-semibold h-9 flex items-center justify-center rounded-lg bg-[#FBD037] hover:bg-[#eac62e] text-[#212121] shadow-sm hover:shadow transition-all"
            />
          )}
        </form>
        {isLoadingProductFetch ? (
          <CardSkeleton rows={6} />
        ) : products?.products?.length ? (
          <div>
            <div className="h-[calc(100vh-10rem)] overflow-y-auto space-y-6 scrollbar-clean">
              {!isOpen && (
                <div className="group  rounded-2xl bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl border border-[#D2D2D2]/70 p-4 transition-all duration-300 space-y-3">
                  <div className="absolute inset-0 rounded-3xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />

                  <div className="space-y-3 mt-3">
                    <div className="flex items-center gap-4">
                      <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={allSelected}
                          onChange={(e) => handleSelectAll(e.target.checked)}
                          ref={(input) => {
                            if (input) input.indeterminate = someSelected;
                          }}
                          className="h-4 w-4 rounded border-slate-300 accent-[#FBD037] text-[#FBD037] focus:ring-[#FBD037]/30"
                        />
                        <span>
                          {allSelected ? t("Deselect All") : t("Select All")}
                        </span>
                      </label>
                      <span className="text-xs text-slate-400">
                        {products.totalItems} {t("products")}
                      </span>
                    </div>
                    {products.products.map((c: any, i: number) => {
                      const productOptionValueKeys =
                        getProductOptionValueKeys(c);
                      const selectedCount = productOptionValueKeys.filter(
                        (id: any) => selectedOptionValues.includes(id),
                      ).length;
                      const allSelectedForProduct =
                        productOptionValueKeys.length > 0 &&
                        selectedCount === productOptionValueKeys.length;
                      const someSelectedForProduct =
                        selectedCount > 0 && !allSelectedForProduct;

                      const idNum = parseInt(c.id, 10);
                      const isExpanded = expandedRows.has(idNum);
                      const hasOptions = c.options && c.options.length > 0;
                      const hasConsistedProducts =
                        c.consisted_products && c.consisted_products.length > 0;

                      return (
                        <div
                          key={c.id}
                          className={`bg-white border rounded-2xl shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden ${
                            someSelectedForProduct || allSelectedForProduct
                              ? "border-[#FBD037] ring-1 ring-[#FBD037]/40"
                              : "border-slate-200"
                          }`}
                        >
                          {/* Card header - always visible */}
                          <div className="flex items-center gap-3 p-4">
                            {/* Checkbox */}
                            <div className="flex-shrink-0">
                              <label className="relative flex items-center cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={allSelectedForProduct}
                                  onChange={(e) =>
                                    handleSelectProductOptionValues(
                                      c.id,
                                      e.target.checked,
                                    )
                                  }
                                  ref={(input) => {
                                    if (input) {
                                      input.indeterminate =
                                        someSelectedForProduct;
                                    }
                                  }}
                                  className="h-5 w-5 rounded border-slate-300 accent-[#FBD037] text-[#FBD037] focus:ring-[#FBD037]/30"
                                />
                              </label>
                            </div>

                            {/* Thumbnail */}
                            <div className="flex-shrink-0">
                              <img
                                alt={c.name?.en || c.name?.ar || "Product"}
                                src={
                                  c.thumbnail ||
                                  c.main_image ||
                                  "https://images.unsplash.com/photo-1629198688000-71f23e745b6e?fm=jpg&q=60&w=3000"
                                }
                                className="w-14 h-14 rounded-xl object-cover border border-slate-100"
                              />
                            </div>

                            {/* Name & details */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <h3 className="font-semibold text-slate-800 truncate">
                                  {typeof c.name === "object"
                                    ? c.name?.en || c.name?.ar
                                    : c.name}
                                </h3>
                                <span className="text-xs text-slate-400 font-mono bg-slate-100 px-2 py-0.5 rounded-full truncate max-w-[120px]">
                                  {t("SKU")}: {c.sku || "-"}
                                </span>
                              </div>
                              <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                                <span>
                                  {c.categories?.[0]
                                    ? typeof c.categories[0].name === "object"
                                      ? c.categories[0].name?.en ||
                                        c.categories[0].name?.ar
                                      : c.categories[0].name
                                    : t("No category")}
                                </span>
                                <span className="flex items-center gap-1">
                                  <span className="line-through text-slate-400">
                                    {c.regular_price?.amount}{" "}
                                    {c.regular_price?.currency?.toUpperCase()}
                                  </span>
                                  <span className="text-[#4CAF50] font-semibold">
                                    {c.price?.amount}{" "}
                                    {c.price?.currency?.toUpperCase()}
                                  </span>
                                </span>
                                <span
                                  className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                                    c.alreadyExist
                                      ? "bg-amber-50 text-amber-600 border border-amber-200"
                                      : "bg-emerald-50 text-emerald-600 border border-emerald-200"
                                  }`}
                                >
                                  {c.alreadyExist ? t("Exists") : t("New")}
                                </span>
                                <span
                                  className={`px-2 py-0.5 rounded-full text-[10px] font-medium border ${
                                    c.consisted_products?.length
                                      ? "bg-purple-50 text-purple-600 border-purple-200"
                                      : c.options?.length
                                        ? "bg-blue-50 text-blue-600 border-blue-200"
                                        : "bg-slate-100 text-slate-600 border-slate-300"
                                  }`}
                                >
                                  {c.consisted_products?.length
                                    ? t("Bundle")
                                    : c.options?.length
                                      ? t("Parent")
                                      : t("Simple")}
                                </span>
                              </div>
                            </div>

                            {/* Expand / collapse button */}
                            {(hasOptions || hasConsistedProducts) && (
                              <button
                                onClick={() => toggleRow(c.id)}
                                className="flex-shrink-0 p-2 rounded-full hover:bg-slate-100 text-slate-500 transition-colors"
                                title={
                                  isExpanded ? t("Hide details") : t("Show details")
                                }
                              >
                                {isExpanded ? (
                                  <IconChevronUp size={18} />
                                ) : (
                                  <IconChevronDown size={18} />
                                )}
                              </button>
                            )}
                          </div>

                          {/* Expanded content */}
                          {isExpanded && renderExpandedContent(c)}
                        </div>
                      );
                    })}
                  </div>

                  {products?.totalPages > 1 && (
                    <div className="mt-6 flex justify-center">
                      <Pagination
                        currentPage={pageNo}
                        totalPages={products?.totalPages}
                        fetchData={handlePaginationFetch}
                      />
                    </div>
                  )}
                </div>
              )}
              {isOpen && (
                <div className="relative rounded-3xl shadow-sm border flex flex-col overflow-hidden bg-white">
                  <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 ">
                    <h2 className="text-xl font-semibold text-[#212121]">
                      {t("Add Inventory")}
                    </h2>

                    <div className="flex items-center gap-3">
                      <button
                        onClick={handleAddProductSubmit(handleAddProduct)}
                        className="px-4 py-2 bg-[#FBD037] text-[#212121] rounded-lg"
                      >
                        {t("Add")}
                      </button>
                      <button
                        onClick={() => setIsOpen(false)}
                        className="p-2 rounded-lg border border-[#D2D2D2]"
                      >
                        <IconX size={20} />
                      </button>
                    </div>
                  </div>

                  <AddProductFetchModal
                    open={isOpen}
                    onClose={() => setIsOpen(false)}
                    setValue={setProductValue}
                    watch={productWatch}
                    register={addProductRegister}
                    errors={productErrors}
                    clearErrors={handleProductClearErrors}
                    categories={allCategory}
                    subCategories={subCategories}
                    products={selectedOptionValues}
                    handleSubmit={handleAddProductSubmit(handleAddProduct)}
                    isPending={isAddingProduct}
                    inline={true}
                  />
                </div>
              )}
            </div>
          </div>
        ) : (
          <NoDataFound mainClassName="" />
        )}
      </div>
    </div>
  );
}
